import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { hydrologyApi } from '../services/api';
import { format, parseISO } from 'date-fns';
import { adToBS, BS_MONTHS_SHORT } from '../utils/bs';
import {
  ResponsiveContainer, ComposedChart, BarChart, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Area, AreaChart,
} from 'recharts';
import type { TooltipProps } from 'recharts';
import type { HydrologyForecast, DayForecast, HourlyForecast } from '../types';
import HydroOverviewChart from '../components/Dashboard/HydroOverviewChart';

// ─── Custom tooltips ─────────────────────────────────────────────────────────

const DispatchTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
  if (!active || !payload?.length) return null;
  const h = payload[0]?.payload as HourlyForecast & { label: string };
  if (!h) return null;
  return (
    <div className="bg-white border border-hydro-border rounded-lg px-3 py-2 text-xs space-y-1 shadow-sm">
      <p className="font-semibold text-slate-700">{label}</p>
      <p className="text-blue-600">Power: {h.power_kw.toLocaleString()} kW ({h.dispatch_pct}%)</p>
      <p className="text-slate-500">Precipitation: {h.precipitation_mm} mm</p>
    </div>
  );
};

// ─── Day selector ─────────────────────────────────────────────────────────────

function DayTab({ day, active, onClick }: { day: DayForecast; active: boolean; onClick: () => void }) {
  const d   = parseISO(day.date);
  const nd  = adToBS(d);
  const isWeekend = [0, 6].includes(d.getDay());
  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center px-4 py-2 rounded-lg text-xs font-medium transition-colors flex-shrink-0 ${
        active
          ? 'bg-brand-600 text-white'
          : 'text-slate-600 hover:bg-slate-100 border border-hydro-border'
      }`}
    >
      <span className="font-semibold">{format(d, 'EEE')}</span>
      <span className={active ? 'text-blue-200' : isWeekend ? 'text-slate-400' : 'text-slate-500'}>
        {nd.getDate()} {BS_MONTHS_SHORT[nd.getMonth()]}
      </span>
    </button>
  );
}

// ─── Hourly dispatch chart ────────────────────────────────────────────────────

function DispatchChart({ day }: { day: DayForecast }) {
  const data = day.hourly.map((h) => ({
    ...h,
    label:         `${String(h.hour).padStart(2,'0')}:00`,
    Power:         h.power_kw,
    Precipitation: h.precipitation_mm,
  }));

  return (
    <div className="card">
      <p className="text-sm font-semibold text-slate-900 mb-4">Dispatch Schedule — {format(parseISO(day.date), 'EEEE')}, {adToBS(parseISO(day.date)).getDate()} {BS_MONTHS_SHORT[adToBS(parseISO(day.date)).getMonth()]}</p>
      <ResponsiveContainer width="100%" height={240}>
        <ComposedChart data={data} margin={{ top: 4, right: 20, left: -5, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 9 }} tickLine={false} interval={1} />
          <YAxis yAxisId="left"  tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} domain={[0, 'dataMax + 500']} />
          <YAxis yAxisId="right" orientation="right" tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} domain={[0, 'dataMax + 2']} />
          <Tooltip content={<DispatchTooltip />} />
          <Bar   yAxisId="left"  dataKey="Power"         name="Power (kW)"         fill="#3b82f6" fillOpacity={0.85} radius={[3,3,0,0]} label={false} />
          <Line  yAxisId="right" dataKey="Precipitation" name="Precipitation (mm)" stroke="#10b981" strokeWidth={2} dot={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Hourly dispatch table ────────────────────────────────────────────────────

function DispatchTable({ day }: { day: DayForecast }) {
  return (
    <div className="card p-0 overflow-hidden">
      <div className="px-4 py-3 border-b border-hydro-border">
        <p className="text-sm font-semibold text-slate-900">Hourly Breakdown</p>
      </div>
      <div className="overflow-x-auto max-h-72">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-hydro-card">
            <tr className="border-b border-hydro-border text-slate-500 uppercase tracking-wider">
              <th className="px-4 py-2 text-left">Hour</th>
              <th className="px-4 py-2 text-left">Precipitation (mm)</th>
              <th className="px-4 py-2 text-left">Dispatch %</th>
              <th className="px-4 py-2 text-left">Power (kW)</th>
            </tr>
          </thead>
          <tbody>
            {day.hourly.map((h) => (
              <tr key={h.hour} className={`table-row ${day.peak_dispatch_hours.includes(h.hour) ? 'bg-amber-50' : ''}`}>
                <td className="px-4 py-2 font-mono font-semibold text-slate-700">
                  {String(h.hour).padStart(2,'0')}:00
                  {day.peak_dispatch_hours.includes(h.hour) && (
                    <span className="ml-1 text-amber-600 text-xs">★</span>
                  )}
                </td>
                <td className="px-4 py-2 font-mono">{h.precipitation_mm.toFixed(2)}</td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-brand-600"
                        style={{ width: `${h.dispatch_pct}%` }}
                      />
                    </div>
                    <span className="font-mono">{h.dispatch_pct}%</span>
                  </div>
                </td>
                <td className="px-4 py-2 font-mono text-slate-900">{h.power_kw.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function Hydrology() {
  const [selectedDay, setSelectedDay] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const { data: forecast, isLoading, error, refetch } = useQuery<HydrologyForecast>({
    queryKey: ['hydrology-forecast'],
    queryFn: () => hydrologyApi.forecast().then(r => r.data.data),
    staleTime: 3 * 60 * 60 * 1000,
    retry: 2,
  });

  const day = forecast?.days[selectedDay];

  const handleRefresh = async () => {
    setRefreshing(true);
    await hydrologyApi.forecast(true);
    await refetch();
    setRefreshing(false);
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Hydrological Forecasting & Dispatch Optimizer</h1>
          <p className="text-xs text-slate-400 mt-0.5">
            Weather-driven inflow forecast · Run-of-river dispatch · 7-day generation schedule
          </p>
        </div>
        <div className="flex items-center gap-3">
          {forecast && (
            <span className="text-xs text-slate-400">
              Updated: {(() => { const d = parseISO(forecast.generated_at); const nd = adToBS(d); return `${nd.getDate()} ${BS_MONTHS_SHORT[nd.getMonth()]} ${format(d, 'HH:mm')}`; })()}
            </span>
          )}
          <button
            onClick={handleRefresh}
            disabled={refreshing || isLoading}
            className="btn-ghost text-xs px-3 py-1"
          >
            {refreshing ? 'Refreshing…' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {isLoading && (
        <div className="card text-center py-16 text-slate-400">
          <p className="text-lg mb-2">🌦</p>
          <p>Fetching weather forecast & computing dispatch schedule…</p>
        </div>
      )}

      {error && (
        <div className="card border border-red-300 bg-red-50 text-center py-12">
          <p className="text-red-700 font-medium">Weather API unavailable</p>
          <p className="text-red-500 text-sm mt-1">Check server connectivity to Open-Meteo API</p>
          <button onClick={() => refetch()} className="btn-ghost text-sm mt-4">Retry</button>
        </div>
      )}

      {forecast && (
        <>
          {/* Plant parameters strip */}
          <div className="card py-3">
            <div className="flex flex-wrap gap-6 items-center text-xs text-slate-500">
              <span>📍 {forecast.location.lat}°N, {forecast.location.lon}°E</span>
              <span>⚡ {forecast.plant_capacity_kw.toLocaleString()} kW rated</span>
              <span>💧 Head: {forecast.plant_head_m} m</span>
              <span>🌊 Rated flow: {forecast.rated_flow_m3s} m³/s</span>
              <span>🌿 Min env. flow: {forecast.min_flow_m3s} m³/s</span>
            </div>
          </div>

          {/* Week summary KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { label: 'Est. Weekly Generation', value: `${(forecast.week_summary.total_generation_kwh / 1000).toFixed(1)} MWh`, icon: '⚡', color: 'text-brand-600' },
              { label: 'Avg Dispatch', value: `${forecast.week_summary.avg_dispatch_pct}%`, icon: '📊', color: 'text-amber-600' },
              { label: 'Best Generation Day', value: forecast.week_summary.peak_day ? (() => { const d = parseISO(forecast.week_summary.peak_day); const nd = adToBS(d); return `${format(d, 'EEE')} ${nd.getDate()} ${BS_MONTHS_SHORT[nd.getMonth()]}`; })() : '—', icon: '🏆', color: 'text-blue-600' },
            ].map((kpi) => (
              <div key={kpi.label} className="card text-center">
                <span className="text-2xl">{kpi.icon}</span>
                <p className={`text-2xl font-bold font-mono mt-1 ${kpi.color}`}>{kpi.value}</p>
                <p className="text-xs text-slate-500 mt-1">{kpi.label}</p>
              </div>
            ))}
          </div>

          {/* 7-day overview chart */}
          <HydroOverviewChart days={forecast.days} />

          {/* Day tabs */}
          <div className="flex gap-2 overflow-x-auto pb-1">
            {forecast.days.map((d, i) => (
              <DayTab
                key={d.date}
                day={d}
                active={i === selectedDay}
                onClick={() => setSelectedDay(i)}
              />
            ))}
          </div>

          {/* Day detail */}
          {day && (
            <>
              {/* Day KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { label: 'Total Precipitation', value: `${day.total_precip_mm.toFixed(2)} mm`, cls: 'text-cyan-600' },
                  { label: 'Est. Generation', value: `${(day.total_generation_kwh / 1000).toFixed(2)} MWh`, cls: 'text-brand-600' },
                ].map((kpi) => (
                  <div key={kpi.label} className="card text-center">
                    <p className="text-xs text-slate-500 uppercase tracking-wider">{kpi.label}</p>
                    <p className={`text-2xl font-bold font-mono mt-2 ${kpi.cls}`}>{kpi.value}</p>
                  </div>
                ))}
              </div>

              {/* High dispatch hours info */}
              {day.peak_dispatch_hours.length > 0 && (
                <div className="card border border-blue-300 bg-blue-50 flex items-start gap-3 py-3">
                  <span className="text-lg">⚡</span>
                  <div>
                    <p className="text-sm font-semibold text-blue-800">High Precipitation Hours — Full Dispatch Expected</p>
                    <p className="text-xs text-blue-700 mt-0.5">
                      Running above 80% capacity between{' '}
                      {day.peak_dispatch_hours.map((h) => `${String(h).padStart(2,'0')}:00–${String(h+1).padStart(2,'0')}:00`).join(', ')}
                    </p>
                  </div>
                </div>
              )}

              {/* Dispatch chart */}
              <DispatchChart day={day} />

              {/* Precipitation area chart */}
              <div className="card">
                <p className="text-sm font-semibold text-slate-900 mb-4">Hourly Precipitation Forecast (mm)</p>
                <ResponsiveContainer width="100%" height={180}>
                  <AreaChart
                    data={day.hourly.map((h) => ({
                      label:         `${String(h.hour).padStart(2,'0')}:00`,
                      Precipitation: h.precipitation_mm,
                    }))}
                    margin={{ top: 4, right: 10, left: -15, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="gradPrecip" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#10b981" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0.0}  />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 9 }} tickLine={false} interval={3} />
                    <YAxis tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} />
                    <Tooltip
                      contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 11 }}
                      labelStyle={{ color: '#64748b' }}
                      formatter={(v: number) => [`${v.toFixed(2)} mm`, 'Precipitation']}
                    />
                    <Area type="monotone" dataKey="Precipitation" stroke="#10b981" strokeWidth={2}
                      fill="url(#gradPrecip)" dot={false} isAnimationActive={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Hourly table */}
              <DispatchTable day={day} />
            </>
          )}

          {/* Methodology note */}
          <p className="text-xs text-slate-400 text-center">
            Inflow estimated via rational method: Q = C·P·A (C=0.35, A=10 km²) with 3h lag ·
            Run-of-river dispatch: flow clamped between min env. flow and rated capacity ·
            Weather: Open-Meteo API
          </p>
        </>
      )}
    </div>
  );
}
